export type UserRole = 'member' | 'admin';

export type AuthUser = {
  role: UserRole;
  username: string;
};

export type AuthSessionResponse = {
  session: {
    createdAt: string;
  };
  user: AuthUser;
};

export type ApiMeta = {
  appName: string;
  slotGroups: string[];
  status: 'ok';
};

export type PersonSummary = {
  displayName: string;
  icsToken: string;
  id: number;
};

export type PlanSlot = {
  appointmentDate: string;
  currentPersonId: number | null;
  currentPersonName: string | null;
  endsAt: string;
  icsToken: string;
  id: number;
  originalPersonId: number | null;
  originalPersonName: string | null;
  slotGroup: string;
  slotLabel: string | null;
  slotType: 'individual' | 'group_training';
  startsAt: string;
  wantsTrade: boolean;
};

export type AdminPerson = {
  activityPeriods: Array<{
    endsOn: string | null;
    id: number;
    startsOn: string;
  }>;
  createdAt: string;
  displayName: string;
  id: number;
  notes: string | null;
};

export type AppointmentDateRecord = {
  appointmentDate: string;
  createdAt: string;
  id: number;
  isAvailable: boolean;
  isPlanned: boolean;
};

export type PlanningAssignment = {
  appointmentDate: string;
  endsAt: string;
  personId: number | null;
  slotGroup: string;
  slotLabel?: string;
  slotType: 'individual' | 'group_training';
  startsAt: string;
};

export type PlanningPreviewDay = {
  activePeopleCount: number;
  appointmentDate: string;
  assignments: PlanningAssignment[];
  plannedSlotCount: number;
  selectedSlotCountsByPersonId: Record<number, number>;
};

export type PlanningPreview = {
  appointmentDates: string[];
  availableAppointmentDates: string[];
  availableDateCount: number;
  days: PlanningPreviewDay[];
  missingDateCount: number;
  selectedAppointmentDates: string[];
  totalActivePeopleCount: number;
  totalAssignmentCount: number;
  totalPlannedSlotCount: number;
};

export type PlanningRunResult = {
  appointmentDates: string[];
  assignmentCount: number;
  days: PlanningPreviewDay[];
  selectedSlotCountsByPersonId: Record<number, number>;
  status: 'planned';
};

export type StatsItem = {
  currentAssignedSlots: number;
  pastSlots: number;
  displayName: string;
  expectedHistoricalSlots: number;
  futureExpectedHistoricalSlots: number;
  futureHistoricalAssignedSlots: number;
  futurePlannedSlots: number;
  futureOverallFairnessDebt: number;
  futureTimeSlotFairnessDebt: number;
  historicalAssignedSlots: number;
  overallFairnessDebt: number;
  personId: number;
  slotDebtUntilToday: number;
  projectedFairnessTrend: string;
  slotGroupDistribution: {
    frueh: number;
    mittel: number;
    spaet: number;
  };
  fairnessHint: string;
  timeSlotFairnessDebt: number;
};

type RequestOptions = {
  body?: unknown;
  headers?: Record<string, string>;
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  skipUnauthorizedHandler?: boolean;
};

function apiUrl(path: string): string {
  return `${__APP_API_BASE_URL__}${path}`;
}

export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

let unauthorizedHandler: (() => void) | null = null;

function extractErrorMessage(body: string): string {
  const trimmed = body.trim();

  if (!trimmed) {
    return 'Anfrage fehlgeschlagen';
  }

  try {
    const parsed = JSON.parse(trimmed) as unknown;

    if (
      parsed &&
      typeof parsed === 'object' &&
      'message' in parsed &&
      typeof (parsed as { message: unknown }).message === 'string'
    ) {
      return (parsed as { message: string }).message;
    }
  } catch {
    // The body is not JSON; fall back to the raw text below.
  }

  return trimmed;
}

export function setUnauthorizedHandler(handler: (() => void) | null) {
  unauthorizedHandler = handler;
}

async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const hasBody = options.body !== undefined;
  const response = await fetch(apiUrl(path), {
    body: hasBody ? JSON.stringify(options.body) : undefined,
    credentials: 'include',
    headers: {
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...(options.headers ?? {}),
    },
    method: options.method ?? 'GET',
  });

  if (!response.ok) {
    const text = await response.text();

    if (response.status === 401 && !options.skipUnauthorizedHandler) {
      unauthorizedHandler?.();
    }

    throw new ApiError(response.status, extractErrorMessage(text));
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export function login(username: string, password: string) {
  return request<AuthSessionResponse>('/api/auth/login', {
    body: {
      password,
      username,
    },
    method: 'POST',
    skipUnauthorizedHandler: true,
  });
}

export function fetchAuthSession() {
  return request<AuthSessionResponse>('/api/auth/session', {
    skipUnauthorizedHandler: true,
  });
}

export function logout() {
  return request<void>('/api/auth/logout', {
    method: 'POST',
  });
}

export function fetchMeta() {
  return request<ApiMeta>('/api/meta');
}

export function fetchPeople() {
  return request<{ items: PersonSummary[] }>('/api/people');
}

export function fetchPlanForMonth(month: string) {
  return request<{ items: PlanSlot[] }>(`/api/plan?month=${month}`);
}

export function fetchPlanForDate(date: string) {
  return request<{ items: PlanSlot[] }>(`/api/plan?date=${date}`);
}

export function fetchPersonSlots(personId: number, includePast: boolean) {
  return request<{ items: PlanSlot[] }>(
    `/api/people/${personId}/slots?includePast=${includePast ? 'true' : 'false'}`,
  );
}

export function fetchTradeSlots() {
  return request<{ items: PlanSlot[] }>('/api/trade-slots');
}

export function updateTradeStatus(slotId: number, wantsTrade: boolean) {
  return request(`/api/slots/${slotId}/trade`, {
    body: {
      wantsTrade,
    },
    method: 'POST',
  });
}

export function swapSlots(
  offerSlot: { currentPersonId: number | null; id: number },
  ownSlot: { currentPersonId: number | null; id: number },
) {
  return request('/api/slots/swap', {
    body: {
      firstSlotId: offerSlot.id,
      secondSlotId: ownSlot.id,
      expectedFirstPersonId: offerSlot.currentPersonId,
      expectedSecondPersonId: ownSlot.currentPersonId,
    },
    method: 'POST',
  });
}

export function fetchAdminPeople() {
  return request<{ items: AdminPerson[] }>('/api/admin/people');
}

export function createAdminPerson(input: {
  displayName: string;
  notes?: string;
  startsOn: string;
}) {
  return request<AdminPerson>('/api/admin/people', {
    body: input,
    method: 'POST',
  });
}

export function createActivityPeriod(
  personId: number,
  input: { endsOn?: string; startsOn: string },
) {
  return request(`/api/admin/people/${personId}/activity-periods`, {
    body: input,
    method: 'POST',
  });
}

export function updateActivityPeriod(
  periodId: number,
  input: { endsOn: string | null },
) {
  return request(`/api/admin/activity-periods/${periodId}`, {
    body: input,
    method: 'PATCH',
  });
}

export function fetchAvailableDates() {
  return request<{ items: AppointmentDateRecord[] }>(
    '/api/admin/available-dates',
  );
}

export function setAvailableDateAvailability(
  appointmentDate: string,
  isAvailable: boolean,
) {
  return request<AppointmentDateRecord>(
    `/api/admin/available-dates/${appointmentDate}`,
    {
      body: {
        isAvailable,
      },
      method: 'PUT',
    },
  );
}

export type ClearPlanResult = {
  appointmentDate: string;
  deletedSlotCount: number;
  status: 'cleared';
};

export function removeAppointmentDatePlan(appointmentDate: string) {
  return request<ClearPlanResult>(`/api/admin/plans/${appointmentDate}`, {
    method: 'DELETE',
  });
}

export function fetchPlanningPreviewForAppointmentDates(
  appointmentDates: string[],
) {
  return request<PlanningPreview>('/api/admin/plans/preview', {
    body: {
      appointmentDates,
    },
    method: 'POST',
  });
}

export function planAppointmentDates(appointmentDates: string[]) {
  return request<PlanningRunResult>('/api/admin/plans', {
    body: {
      appointmentDates,
    },
    method: 'POST',
  });
}

export function updateAdminSlot(
  slotId: number,
  input: { currentPersonId: number | null; wantsTrade: boolean },
) {
  return request(`/api/admin/slots/${slotId}`, {
    body: input,
    method: 'PATCH',
  });
}

export function fetchStats() {
  return request<{ items: StatsItem[] }>('/api/admin/stats');
}

export function personIcsUrl(personId: number, token: string) {
  return apiUrl(
    `/api/people/${personId}/ics?token=${encodeURIComponent(token)}`,
  );
}

export function slotIcsUrl(slotId: number, token: string) {
  return apiUrl(`/api/slots/${slotId}/ics?token=${encodeURIComponent(token)}`);
}
